// ===========================================
// Teacher Performance Report Page
// ===========================================
// Comprehensive teacher performance report with filters, summary, charts, table, and export

import { useState } from 'react';
import { useSchoolWeek } from '../../../hooks/useSchoolWeek';
import {
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

import type { TeacherPerformanceFilters } from '../../../api/reports.api';
import {
  useTeacherPerformanceReport,
  useExportTeacherPerformanceReport,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import TeacherPerformanceFiltersComponent from '../../../components/reports/TeacherPerformanceFilters';
import TeacherPerformanceSummaryCards from '../../../components/reports/TeacherPerformanceSummaryCards';
import TeacherComparisonChart from '../../../components/reports/TeacherComparisonChart';
import TeacherRetentionChart from '../../../components/reports/TeacherRetentionChart';
import TeacherRevenueChart from '../../../components/reports/TeacherRevenueChart';
import TeacherPerformanceTable from '../../../components/reports/TeacherPerformanceTable';
import ExportButtons from '../../../components/reports/ExportButtons';

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherPerformancePage() {
  // The page opens on the SCHOOL's current week (Monday–Sunday) rather than a
  // "select filters" card — an empty card reads as "no data". The heading says
  // which week, leading with the term week when one applies.
  const { week, describe } = useSchoolWeek();
  const [filters, setFilters] = useState<TeacherPerformanceFilters>(() => ({ ...week }));
  const [appliedFilters, setAppliedFilters] = useState<TeacherPerformanceFilters>(() => ({ ...week }));
  // Remounts the filter card on Reset so its date pickers show the week again.
  const [filtersKey, setFiltersKey] = useState(0);

  // Fetch report data (only when filters are applied)
  const { data: reportData, isLoading, isError, refetch } = useTeacherPerformanceReport(appliedFilters);
  const exportMutation = useExportTeacherPerformanceReport();

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters({ ...week });
    setAppliedFilters({ ...week });
    setFiltersKey((k) => k + 1);
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    exportMutation.mutate({ format, filters: appliedFilters });
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Teacher Performance Report"
        subtitle={describe(appliedFilters) ?? "Analyze teacher effectiveness, retention, and revenue contribution"}
        icon={<PersonIcon />}
        action={
          reportData && (
            <ExportButtons
              onExport={handleExport}
              loading={exportMutation.isPending}
              disabled={!reportData}
            />
          )
        }
      />

      {/* Filters */}
      <TeacherPerformanceFiltersComponent
        key={filtersKey}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State — never an empty table: an error is not "no data" */}
      {!isLoading && isError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The teacher performance report could not be loaded. Nothing below reflects this period.
        </Alert>
      )}

      {/* Report Content */}
      {!isLoading && reportData && (
        <>
          {/* Summary Cards */}
          <TeacherPerformanceSummaryCards summary={reportData.summary} />

          {/* Charts Grid Row 1 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Teacher Comparison Chart - Full width */}
            <Grid item xs={12}>
              <TeacherComparisonChart teachers={reportData.teachers} />
            </Grid>
          </Grid>

          {/* Charts Grid Row 2 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Teacher Retention Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <TeacherRetentionChart data={reportData.retentionData} />
            </Grid>

            {/* Teacher Revenue Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <TeacherRevenueChart data={reportData.revenueData} />
            </Grid>
          </Grid>

          {/* Teacher Performance Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Teacher Details
                </Typography>
              </Box>
              <TeacherPerformanceTable teachers={reportData.teachers} />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
